import type { LinkTarget } from "../types";

export type LinkValue = LinkTarget | string | URL | null | undefined;

/** Resolves a link value (slug, external, URL) into a usable href. */
export function resolveLinkHref(target: LinkValue): string | undefined {
  if (!target) return undefined;

  if (typeof target === "string" || target instanceof URL) {
    const normalized = target.toString().trim();
    return normalized.length ? normalized : undefined;
  }

  if (typeof target === "object") {
    const external = target.external?.toString().trim();
    if (external) {
      return external;
    }

    const slug = target.slug?.toString().trim();
    if (slug) {
      return slug.startsWith("/") ? slug : `/${slug}`;
    }

    // pageId is reserved for future slug syncing; intentionally ignored for now.
  }

  return undefined;
}

/** Checks whether the link points to an external URL (http/https). */
export function isExternalHref(target: LinkValue): boolean {
  const href = resolveLinkHref(target);
  return href ? /^https?:\/\//i.test(href) : false;
}

/**
 * Treats placeholder destinations as "not configured". A link is unconfigured when it
 * resolves to nothing, to the bare home page ("/"), or to an empty anchor ("#", "#top").
 * The CMS uses those exact values for buttons whose real page isn't set up yet (e.g.
 * "Emploi" → "#", "Musée" → "/"), and a genuine nav/footer item never legitimately points
 * at the bare homepage — the logo already does that. Used by the dev-placeholder layer so
 * such links open the preview modal and are hidden entirely in production.
 */
export function isUnconfiguredHref(target: LinkValue): boolean {
  const href = resolveLinkHref(target);
  if (!href) return true;
  const trimmed = href.trim();
  return trimmed === "/" || trimmed.startsWith("#");
}

/** A usable destination href, or undefined when the link is unconfigured (see {@link isUnconfiguredHref}). */
export function resolveConfiguredHref(target: LinkValue): string | undefined {
  return isUnconfiguredHref(target) ? undefined : resolveLinkHref(target);
}


